// src/components/dialogs/ErrorDialog.js
import React, { useState, useEffect } from 'react';
import '../../styles/ErrorDialog.css';

/**
 * エラータイプごとのスタイル設定
 */
const ERROR_TYPES = {
  SERVER_ERROR: {
    title: 'サーバーエラー',
    className: 'error-dialog--server'
  },
  CLIENT_ERROR: {
    title: 'リクエストエラー',
    className: 'error-dialog--client'
  },
  NETWORK_ERROR: {
    title: 'ネットワークエラー',
    className: 'error-dialog--network'
  },
  TIMEOUT_ERROR: {
    title: 'タイムアウトエラー',
    className: 'error-dialog--timeout'
  },
  VALIDATION_ERROR: {
    title: '入力エラー',
    className: 'error-dialog--validation'
  },
  SESSION_EXPIRED: {
    title: 'セッション期限切れ',
    className: 'error-dialog--session'
  },
  DEFAULT: {
    title: 'エラー',
    className: 'error-dialog--default'
  }
};

/**
 * エラーダイアログコンポーネント
 */
const ErrorDialog = ({ 
  isOpen = false, 
  onClose = () => {},
  errorData = null,
  customConfig = {}
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [animationClass, setAnimationClass] = useState('');

  // ダイアログの表示/非表示アニメーション制御
  useEffect(() => {

    let showTimer;
    let hideTimer;
    
    if (isOpen) {
      setIsVisible(true);
      showTimer = setTimeout(() => setAnimationClass('error-dialog--show'), 10);
    } else {
      setAnimationClass('error-dialog--hide');
      hideTimer = setTimeout(() => {
        setIsVisible(false);
        setAnimationClass('');
      }, 300);
    }
    
    // クリーンアップ: タイマーをクリア
    return () => {
      if (showTimer) clearTimeout(showTimer);
      if (hideTimer) clearTimeout(hideTimer);
    };
  }, [isOpen]);

  // ESCキーでダイアログを閉じる
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      // ダイアログが開いている間はページスクロールを無効化
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isVisible) return null;

  // エラーデータの解析
  const errorType = errorData?.type || 'DEFAULT';
  const errorConfig = ERROR_TYPES[errorType] || ERROR_TYPES.DEFAULT;
  const finalConfig = { ...errorConfig, ...customConfig }; //ダイアログ生成時にcustomconfigが指定されている場合、上書きする

  const handleClose = () => {
    // customConfig.customConfigにonCustomCloseがある場合は優先的に実行
    if (errorData?.customConfig?.onCustomClose) {
      errorData.customConfig.onCustomClose();
    }
    onClose();
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleRetry = () => {
    if (errorData?.onRetry) {
      errorData.onRetry();
    }
    handleClose();
  };

  return (
    <div 
      className={`error-dialog-backdrop ${animationClass}`}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="error-dialog-title"
    >
      <div 
        className={`error-dialog ${finalConfig.className} ${animationClass}`}
      >
        {/* ヘッダー */}
        <div className="error-dialog__header">
          <h2 id="error-dialog-title" className="error-dialog__title">
            {finalConfig.title}
          </h2>
          <button 
            className="error-dialog__close"
            onClick={handleClose}
            aria-label="ダイアログを閉じる"
          >
            ✕
          </button>
        </div>

        {/* コンテンツ */}
        <div className="error-dialog__content">
          <div className="error-dialog__message">
            {finalConfig.message || errorData?.message || 'エラーが発生しました。'}
          </div>
        </div>

        {/* アクションボタン */}
        <div className="error-dialog__actions">
          {errorData?.onRetry && (
            <button 
              className="error-dialog__button error-dialog__button--retry"
              onClick={handleRetry}
            >
              再試行
            </button>
          )}
          
          <button 
            className="error-dialog__button error-dialog__button--close"
            onClick={handleClose}
          >
            閉じる
          </button>
        </div>
      </div>
    </div>
  );
};

export default ErrorDialog;